<template>
    <component
        :is="dynamicComponent"
        v-bind="{
            ...componentProps,
            ...$attrs,
            ...(actionType === 'action' || actionType === 'link' ? { onClick: emitClick } : {}),
        }"
        :class="[
            'dropdown-menu-item',
            sizeClass,
            typeClass,
            hasSeparator ? 'has-separator' : undefined,
            helpText ? 'has-help-text' : undefined,
        ]"
    >
        <div class="item-content">
            <div class="icon-container">
                <i v-if="icon && (type === 'icon' || type === 'danger-icon')" :class="[icon, iconColorClass]"></i>
            </div>
            <template v-if="type === 'image'">
                <img
                    v-if="imgUrl && isImageLoaded"
                    :src="imgUrl"
                    :alt="alt"
                    class="item-image"
                    @load="handleImageLoad"
                    @error="handleImageError"
                />
                <img v-else :src="missingImagePlaceholder" :alt="alt" class="item-image" />
            </template>

            <span class="item-text">
                {{ text }}
            </span>
        </div>

        <p v-if="helpText" class="help-text">
            {{ helpText }}
        </p>
    </component>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { RouterLink } from "vue-router";
import missingImagePlaceholder from "@/assets/images/placeholders/missing-image-placeholder.png";

import type { DropdownActionType, DropdownItemSize, DropdownItemType } from "@/types/dropdowns";

export default defineComponent({
    name: "DropdownMenuItem",
    inheritAttrs: false,

    emits: ["click", "close"],

    props: {
        actionType: {
            type: String as PropType<DropdownActionType>,
            default: "link",
        },
        text: {
            type: String as PropType<string>,
            default: "Menu item text",
        },
        icon: {
            type: String as PropType<string>,
            default: "",
        },
        size: {
            type: String as PropType<DropdownItemSize>,
            default: "md",
        },
        type: {
            type: String as PropType<DropdownItemType>,
            default: "text",
        },
        imgUrl: {
            type: String as PropType<string>,
            default: "",
        },
        alt: {
            type: String as PropType<string>,
            default: "Menu item image",
        },
        helpText: {
            type: String as PropType<string>,
            default: "",
        },
        to: {
            type: String as PropType<string>,
            default: "/",
        },
        isExternal: {
            type: Boolean as PropType<boolean>,
            default: false,
        },
        hasSeparator: {
            type: Boolean as PropType<boolean>,
            default: false,
        },
    },

    data() {
        return {
            isImageLoaded: true,
            missingImagePlaceholder,
        };
    },

    computed: {
        sizeClass(): string {
            const sizeVariant: Record<string, string> = {
                md: "size-md",
                lg: "size-lg",
            };
            return sizeVariant[this.size] || "size-md";
        },

        typeClass(): string {
            const typeVariant: Record<string, string> = {
                text: "type-default",
                icon: "type-default",
                image: "type-default",
                danger: "type-danger",
                "danger-icon": "type-danger",
            };

            return typeVariant[this.type] || "type-default";
        },

        iconColorClass(): string | undefined {
            const colorVariant: Record<string, string | undefined> = {
                icon: "icon-neutral",
                text: "icon-neutral",
                image: undefined,
                danger: undefined,
                "danger-icon": "icon-danger",
            };

            return colorVariant[this.type];
        },

        dynamicComponent() {
            if (this.actionType === "link") {
                return RouterLink;
            }
            if (this.actionType === "action") {
                return "div";
            }
            return "div";
        },

        componentProps() {
            if (this.actionType === "link") {
                return {
                    to: this.to,
                    target: this.isExternal ? "_blank" : "_self",
                    rel: this.isExternal ? "noopener noreferrer" : undefined,
                };
            }
            return {};
        },
    },

    methods: {
        emitClick: function () {
            if (this.actionType === "action" || this.actionType === "link") {
                this.$emit("click");
                this.$emit("close");
            }
        },

        handleImageLoad: function () {
            this.isImageLoaded = true;
        },

        handleImageError: function () {
            this.isImageLoaded = false;
        },
    },
});
</script>

<style>
@import "@/style/components/dropdowns.css";
</style>
